import { useMemo, useState } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Cpu, Check, Search } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { apiService } from '@/core/api/service';
import { cn } from '@/lib/utils';
import type { Tenant, AvailableModel, TenantChatConfig } from '@/core/models';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

export const Route = createFileRoute('/llm-models')({
  head: () => ({
    meta: [{ title: 'Modelli LLM · Replya Internal Backoffice' }],
  }),
  component: LlmModelsPage,
});

function formatPricePerM(value: number | null): string {
  if (value === null) return '';
  if (value === 0) return 'gratis';
  return `$${value < 0.01 ? value.toFixed(4) : value.toFixed(2)}`;
}

function LlmModelsPage() {
  const queryClient = useQueryClient();
  const [pickerTenant, setPickerTenant] = useState<Tenant | null>(null);
  const [search, setSearch] = useState('');
  const [pickerReasoning, setPickerReasoning] = useState(false);

  const tenantsQuery = useQuery({ queryKey: ['tenants'], queryFn: () => apiService.listTenants() });
  const tenants = useMemo(() => tenantsQuery.data ?? [], [tenantsQuery.data]);

  const configQueries = useQueries({
    queries: tenants.map((t) => ({
      queryKey: ['tenantChatConfig', t.tenantId],
      queryFn: () => apiService.getTenantChatConfig(t.tenantId),
    })),
  });

  const modelsQuery = useQuery({
    queryKey: ['availableModels'],
    queryFn: () => apiService.listAvailableModels(),
    staleTime: 30 * 60 * 1000,
  });

  const defaultModel = modelsQuery.data?.defaultModel ?? '';
  const globalDefaultOption: AvailableModel = useMemo(
    () => ({
      id: '',
      name: defaultModel ? `Default globale (${defaultModel})` : 'Default globale',
      provider: '',
      promptPricePerM: null,
      completionPricePerM: null,
    }),
    [defaultModel],
  );

  const closePicker = () => {
    setPickerTenant(null);
    setSearch('');
    setPickerReasoning(false);
  };

  const openPicker = (tenant: Tenant, config?: TenantChatConfig) => {
    setPickerTenant(tenant);
    setPickerReasoning(!!config?.reasoningEnabled);
  };

  const mutation = useMutation({
    mutationFn: ({
      tenantId,
      chatModel,
      routerModel,
      reasoningEnabled,
    }: {
      tenantId: string;
      chatModel: string;
      routerModel: string;
      reasoningEnabled: boolean;
    }) => apiService.updateTenantChatConfig(tenantId, { chatModel, routerModel, reasoningEnabled }),
    onSuccess: (_, variables) => {
      toast.success('Configurazione aggiornata');
      queryClient.invalidateQueries({ queryKey: ['tenantChatConfig', variables.tenantId] });
      closePicker();
    },
    onError: () => {
      toast.error('Errore durante il salvataggio della configurazione');
    },
  });

  const reasoningMutation = useMutation({
    mutationFn: ({
      tenantId,
      chatModel,
      routerModel,
      reasoningEnabled,
    }: {
      tenantId: string;
      chatModel: string;
      routerModel: string;
      reasoningEnabled: boolean;
    }) => apiService.updateTenantChatConfig(tenantId, { chatModel, routerModel, reasoningEnabled }),
    onSuccess: (_, variables) => {
      toast.success(`Reasoning ${variables.reasoningEnabled ? 'abilitato' : 'disabilitato'}`);
      queryClient.invalidateQueries({ queryKey: ['tenantChatConfig', variables.tenantId] });
    },
    onError: () => {
      toast.error('Errore durante il salvataggio del reasoning');
    },
  });

  const filteredModels = useMemo(() => {
    const all = [globalDefaultOption, ...(modelsQuery.data?.models ?? [])];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (m) => m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q) || m.provider.toLowerCase().includes(q),
    );
  }, [modelsQuery.data, search, globalDefaultOption]);

  const pickerIndex = pickerTenant ? tenants.findIndex((t) => t.tenantId === pickerTenant.tenantId) : -1;
  const pickerConfig = pickerIndex >= 0 ? configQueries[pickerIndex]?.data : undefined;

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader
        title="Modelli LLM"
        subtitle="Assegna il modello di chat usato in produzione per ciascun tenant."
      />

      <Card className="rounded-2xl border bg-surface">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Cpu className="h-4 w-4 text-brand" />
            Tenant
          </CardTitle>
          <CardDescription>
            Clicca su un tenant per scegliere il modello dalla lista completa (via OpenRouter).
            L'effetto è immediato.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tenantsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Caricamento...</p>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Modello chat attuale</TableHead>
                    <TableHead className="w-32">Reasoning</TableHead>
                    <TableHead className="w-32"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((tenant, i) => {
                    const cfg = configQueries[i]?.data;
                    const loading = configQueries[i]?.isLoading;
                    return (
                      <TableRow key={tenant.tenantId}>
                        <TableCell className="font-medium">{tenant.name}</TableCell>
                        <TableCell>
                          {loading ? (
                            <span className="text-sm text-muted-foreground">...</span>
                          ) : cfg?.chatModel ? (
                            <Badge variant="secondary" className="rounded-lg font-mono">
                              {cfg.chatModel}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="rounded-lg font-mono">
                              {defaultModel ? `Default globale (${defaultModel})` : 'Default globale'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {loading ? (
                            <span className="text-sm text-muted-foreground">...</span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Switch
                                id={`reasoning-${tenant.tenantId}`}
                                checked={!!cfg?.reasoningEnabled}
                                disabled={reasoningMutation.isPending}
                                onCheckedChange={(checked) =>
                                  reasoningMutation.mutate({
                                    tenantId: tenant.tenantId,
                                    chatModel: cfg?.chatModel ?? '',
                                    routerModel: cfg?.routerModel ?? '',
                                    reasoningEnabled: checked,
                                  })
                                }
                              />
                              <Label
                                htmlFor={`reasoning-${tenant.tenantId}`}
                                className="text-sm text-muted-foreground cursor-pointer"
                              >
                                {cfg?.reasoningEnabled ? 'Attivo' : 'Disattivo'}
                              </Label>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => openPicker(tenant, cfg)}
                          >
                            Cambia modello
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!tenants.length && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        Nessun tenant trovato
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!pickerTenant}
        onOpenChange={(open) => {
          if (!open) closePicker();
        }}
      >
        <DialogContent className="rounded-2xl border bg-surface max-w-lg">
          <DialogHeader>
            <DialogTitle>Modello per {pickerTenant?.name}</DialogTitle>
            <DialogDescription>
              Scegli un modello dalla lista OpenRouter. "Default globale" usa il modello configurato
              di default nel backend{defaultModel ? ` (${defaultModel})` : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome, provider o ID..."
              className="rounded-xl bg-surface-2 border-border pl-9"
            />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border bg-surface-2 px-3 py-2">
            <div className="flex flex-col gap-0.5">
              <Label htmlFor="picker-reasoning" className="text-sm font-medium">
                Abilita reasoning
              </Label>
              <span className="text-xs text-muted-foreground">
                Attiva il ragionamento avanzato per i modelli che lo supportano.
              </span>
            </div>
            <Switch
              id="picker-reasoning"
              checked={pickerReasoning}
              disabled={mutation.isPending}
              onCheckedChange={setPickerReasoning}
            />
          </div>
          {modelsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Caricamento modelli...</p>
          ) : modelsQuery.isError ? (
            <p className="text-sm text-destructive">
              Errore caricamento modelli: {(modelsQuery.error as Error).message}
            </p>
          ) : (
            <div className="max-h-80 overflow-y-auto space-y-1 -mx-2 px-2">
              {filteredModels.map((model) => {
                const active = (pickerConfig?.chatModel ?? '') === model.id;
                return (
                  <button
                    key={model.id || 'default'}
                    type="button"
                    disabled={mutation.isPending}
                    onClick={() =>
                      pickerTenant &&
                      mutation.mutate({
                        tenantId: pickerTenant.tenantId,
                        chatModel: model.id,
                        routerModel: pickerConfig?.routerModel ?? '',
                        reasoningEnabled: pickerReasoning,
                      })
                    }
                    className={cn(
                      'w-full flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-left text-sm transition-colors hover:bg-surface-2 disabled:opacity-50',
                      active && 'bg-brand/10 text-brand',
                    )}
                  >
                    <span className="flex flex-col overflow-hidden">
                      <span className="font-medium truncate">{model.name}</span>
                      {model.id && (
                        <span className="text-xs text-muted-foreground font-mono truncate">
                          {model.id}
                        </span>
                      )}
                      {(model.promptPricePerM !== null || model.completionPricePerM !== null) && (
                        <span className="text-xs text-muted-foreground">
                          {formatPricePerM(model.promptPricePerM)} in /{' '}
                          {formatPricePerM(model.completionPricePerM)} out · per 1M token
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      {model.provider && (
                        <Badge variant="outline" className="rounded-lg text-xs">
                          {model.provider}
                        </Badge>
                      )}
                      {active && <Check className="h-4 w-4" />}
                    </span>
                  </button>
                );
              })}
              {!filteredModels.length && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Nessun modello trovato
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
